import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

export default function ManageUnlockRequirements() {
  const [token, setToken] = useState();
  const [games, setGames] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [linkedItemIds, setLinkedItemIds] = useState([]);
  const [grantedAccountIds, setGrantedAccountIds] = useState([]);
  const [formError, setFormError] = useState("");

  const [gamesId, setGamesId] = useState("");
  const [requirementType, setRequirementType] = useState("");
  const [platform, setPlatform] = useState("");
  const [name, setName] = useState("");
  const [isAvailable, setIsAvailable] = useState(true);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ games_id: "", requirement_type: "", platform: "", name: "", is_available: true });
  const [itemSearch, setItemSearch] = useState("");

  const startEdit = (req) => {
    setEditingId(req.id);
    setEditForm({ games_id: req.games_id, requirement_type: req.requirement_type, platform: req.platform || "", name: req.name, is_available: req.is_available });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ games_id: "", requirement_type: "", platform: "", name: "" });
  };

  const handleUpdate = async (id) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${id}`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${token.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          games_id: Number(editForm.games_id),
          requirement_type: editForm.requirement_type,
          platform: editForm.platform || null,
          name: editForm.name,
          is_available: editForm.is_available,
        }),
      }
    );
    if (res.ok) {
      cancelEdit();
      await loadRequirements();
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (
        !fetchedToken ||
        !["owner", "management"].includes(fetchedToken.account.role)
      ) {
        navigate("/");
        return;
      }
      setToken(fetchedToken);
    };
    init();
  }, []);

  const loadStaticData = async () => {
    const t = await getTokenInternal();
    const authHeader = t ? { Authorization: `Bearer ${t.access_token}` } : {};
    const [gamesRes, itemsRes, accountsRes] = await Promise.all([
      fetch(`${process.env.REACT_APP_API_HOST}/api/games`),
      fetch(`${process.env.REACT_APP_API_HOST}/api/items`),
      fetch(`${process.env.REACT_APP_API_HOST}/api/accounts`, { headers: authHeader }),
    ]);
    if (gamesRes.ok) setGames(await gamesRes.json());
    if (itemsRes.ok) setItems(await itemsRes.json());
    if (accountsRes.ok) setAccounts(await accountsRes.json());
  };

  const loadRequirements = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/unlock-requirements`
    );
    if (response.ok) setRequirements(await response.json());
  };

  useEffect(() => {
    if (token) {
      loadStaticData();
      loadRequirements();
    }
  }, [token]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setFormError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            games_id: Number(gamesId),
            requirement_type: requirementType,
            platform: platform || null,
            name,
            is_available: isAvailable,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Could not create unlock requirement");
      }
      setRequirementType("");
      setPlatform("");
      setName("");
      setIsAvailable(true);
      await loadRequirements();
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleDelete = async (requirement) => {
    await fetch(
      `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${requirement.id}`,
      { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
    );
    if (expandedId === requirement.id) setExpandedId(null);
    await loadRequirements();
  };

  const handleExpand = async (requirement) => {
    if (expandedId === requirement.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(requirement.id);
    setItemSearch("");

    const [itemsRes, accountsRes] = await Promise.all([
      fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${requirement.id}/items`, {
        headers: { Authorization: `Bearer ${token.access_token}` },
      }),
      fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${requirement.id}/accounts`, {
        headers: { Authorization: `Bearer ${token.access_token}` },
      }),
    ]);
    const linked = itemsRes.ok ? await itemsRes.json() : [];
    const granted = accountsRes.ok ? await accountsRes.json() : [];
    setLinkedItemIds(linked.map((i) => i.item_id));
    setGrantedAccountIds(granted.map((a) => a.account_id));
  };

  const toggleItemLink = async (requirement, itemId, isLinked) => {
    const url = `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${requirement.id}/link-item/${itemId}`;
    await fetch(url, {
      method: isLinked ? "DELETE" : "POST",
      headers: { Authorization: `Bearer ${token.access_token}` },
    });
    setLinkedItemIds((prev) =>
      isLinked ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const gameName = (id) => games.find((g) => g.id === id)?.name || id;

  if (!token) {
    return null;
  }

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4 max-w-3xl">
        <h1 className="text-2xl font-bold mb-2">Unlock Requirements</h1>
        <p className="mb-6 text-gray-300">
          Define things like preorder bonuses or DLC that gate whether an
          item is obtainable. An item with no linked requirement is free for
          everyone. An item linked to multiple requirements is unlocked by
          owning ANY one of them.
        </p>

        <form onSubmit={handleCreate} className="mb-8 flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-sm">Game</label>
            <select
              value={gamesId}
              onChange={(e) => setGamesId(e.target.value)}
              required
              className="p-1 border rounded text-black"
            >
              <option value="">Select a game</option>
              {games.map((game) => (
                <option key={game.id} value={game.id}>{game.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm">Type</label>
            <input
              value={requirementType}
              onChange={(e) => setRequirementType(e.target.value)}
              placeholder="preorder, dlc, etc."
              required
              className="p-1 border rounded text-black"
            />
          </div>
          <div>
            <label className="block text-sm">Platform (optional)</label>
            <input
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              placeholder="PC, Xbox, PlayStation"
              className="p-1 border rounded text-black"
            />
          </div>
          <div>
            <label className="block text-sm">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="PC Preorder"
              required
              className="p-1 border rounded text-black"
            />
          </div>
          <div>
            <label className="block text-sm">Still Available</label>
            <input
              type="checkbox"
              checked={isAvailable}
              onChange={(e) => setIsAvailable(e.target.checked)}
              className="w-4 h-4 mt-1"
            />
          </div>
          <button type="submit" className="p-1 border rounded">Add Requirement</button>
        </form>

        {formError && <p className="text-red-500 mb-4">{formError}</p>}

        {requirements.length === 0 ? (
          <p>No unlock requirements yet.</p>
        ) : (
          <div>
            {requirements.map((requirement) => (
              <div key={requirement.id} className="border border-gray-500 rounded mb-2 p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold">{requirement.name}</span>
                    <span className="ml-2 text-sm text-gray-300">
                      ({gameName(requirement.games_id)} - {requirement.requirement_type}
                      {requirement.platform ? ` - ${requirement.platform}` : ""})
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => editingId === requirement.id ? cancelEdit() : startEdit(requirement)}
                      className="p-1 border rounded text-sm"
                    >
                      {editingId === requirement.id ? "Cancel" : "Edit"}
                    </button>
                    <button
                      onClick={() => handleExpand(requirement)}
                      className="p-1 border rounded text-sm"
                    >
                      {expandedId === requirement.id ? "Hide" : "Manage"}
                    </button>
                    <button
                      onClick={() => handleDelete(requirement)}
                      className="p-1 border rounded text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {editingId === requirement.id && (
                  <div className="mt-2 flex flex-wrap gap-2 items-end border-t border-gray-600 pt-2">
                    <div>
                      <label className="block text-xs text-gray-400">Game</label>
                      <select
                        value={editForm.games_id}
                        onChange={(e) => setEditForm(prev => ({ ...prev, games_id: e.target.value }))}
                        className="p-1 border rounded text-black text-sm"
                      >
                        {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400">Type</label>
                      <input
                        value={editForm.requirement_type}
                        onChange={(e) => setEditForm(prev => ({ ...prev, requirement_type: e.target.value }))}
                        className="p-1 border rounded text-black text-sm w-28"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400">Platform</label>
                      <input
                        value={editForm.platform}
                        onChange={(e) => setEditForm(prev => ({ ...prev, platform: e.target.value }))}
                        placeholder="optional"
                        className="p-1 border rounded text-black text-sm w-28"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400">Name</label>
                      <input
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="p-1 border rounded text-black text-sm w-36"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400">Still Available</label>
                      <input
                        type="checkbox"
                        checked={editForm.is_available}
                        onChange={(e) => setEditForm(prev => ({ ...prev, is_available: e.target.checked }))}
                        className="w-4 h-4 mt-1"
                      />
                    </div>
                    <button
                      onClick={() => handleUpdate(requirement.id)}
                      className="p-1 border rounded text-sm bg-green-700 hover:bg-green-600"
                    >
                      Save
                    </button>
                  </div>
                )}

                {expandedId === requirement.id && (
                  <div className="mt-3 flex gap-6">
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">
                        Items gated by this ({linkedItemIds.length} linked)
                      </h3>
                      <input
                        type="text"
                        placeholder="Search items..."
                        value={itemSearch}
                        onChange={(e) => setItemSearch(e.target.value)}
                        className="mb-2 p-1 w-full border rounded text-black text-sm"
                      />
                      <div className="max-h-48 overflow-y-auto text-sm">
                        {(() => {
                          const gameItems = items.filter(i => i.games_id === requirement.games_id);
                          const filtered = gameItems.filter(i =>
                            i.name.toLowerCase().includes(itemSearch.toLowerCase())
                          );
                          const linked = filtered
                            .filter(i => linkedItemIds.includes(i.id))
                            .sort((a, b) => a.name.localeCompare(b.name));
                          const unlinked = filtered
                            .filter(i => !linkedItemIds.includes(i.id))
                            .sort((a, b) => a.name.localeCompare(b.name));
                          return [...linked, ...unlinked].map(item => {
                            const isLinked = linkedItemIds.includes(item.id);
                            return (
                              <label key={item.id} className="flex items-center gap-1 py-0.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isLinked}
                                  onChange={() => toggleItemLink(requirement, item.id, isLinked)}
                                  className="mr-1"
                                />
                                <span className={isLinked ? "font-semibold" : "text-gray-400"}>
                                  {item.name}
                                </span>
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">
                        Accounts that own this ({grantedAccountIds.length})
                      </h3>
                      <div className="max-h-48 overflow-y-auto text-sm">
                        {grantedAccountIds.length === 0 ? (
                          <p className="text-gray-400 italic">None yet.</p>
                        ) : (
                          accounts
                            .filter(a => grantedAccountIds.includes(a.id))
                            .sort((a, b) => a.username.localeCompare(b.username))
                            .map(account => (
                              <div key={account.id} className="py-0.5 text-gray-200">
                                {account.username}
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
